import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SpeechRecognitionService {
  // Instance of the browser's speech recognition engine
  private recognition: any | null = null;

  // Flag to indicate whether user has requested listening to continue
  private isListening = false;

  // Flag to indicate if recognition engine is actively running
  private isRecognitionRunning = false;

  // Flag to ensure recognition has truly started (helps skip noisy false starts)
  private hasStarted = false;

  constructor(private zone: NgZone) {
    // Grab speech recognition constructor from the global window object
    const { webkitSpeechRecognition, SpeechRecognition } = window as any;

    // Instantiate the engine (cross-browser fallback)
    this.recognition = new (webkitSpeechRecognition || SpeechRecognition)();

    // Configure recognition settings if available
    if (this.recognition) {
      this.recognition.lang = 'en-US'; // Language setting
      this.recognition.interimResults = false; // Emit only final results (no live/interim)
      this.recognition.maxAlternatives = 1; // Only the best guess is returned

      // Triggered when recognition engine starts
      this.recognition.onstart = () => {
        this.isRecognitionRunning = true;
      };

      // Triggered when recognition ends (naturally or manually)
      this.recognition.onend = () => {
        this.isRecognitionRunning = false;

        // Auto-restart if user hasn't stopped listening
        if (this.isListening) {
          try {
            this.recognition!.start();
          } catch (e) {
            console.warn('Recognition restart failed:', e);
          }
        }
      };
    }
  }

  /**
   * Starts the speech recognition and returns an Observable stream of results
   */
  start(): Observable<string> {
    return new Observable((observer) => {
      // Abort if speech recognition is not available
      if (!this.recognition) {
        observer.error('Speech Recognition not supported.');
        return;
      }

      // Set internal flags to track listening session
      this.isListening = true;
      this.hasStarted = false;

      // Local buffer to accumulate partial/interim results
      let interimBuffer = '';

      /**
       * Triggered when the recognition engine starts
       */
      this.recognition.onstart = () => {
        this.isRecognitionRunning = true;
        this.hasStarted = true;
        console.log('[Speech] onstart');
      };

      /**
       * Triggered when the recognition session ends
       */
      this.recognition.onend = () => {
        this.isRecognitionRunning = false;
        console.log('[Speech] onend');

        if (this.isListening) {
          // Auto-restart with a slight delay to avoid rapid fire loops
          setTimeout(() => {
            if (!this.isRecognitionRunning) {
              try {
                this.recognition!.start();
              } catch (e) {
                console.warn('Restart failed:', e);
              }
            }
          }, 200);
        } else {
          // If user stopped it, complete the Observable
          this.zone.run(() => observer.complete());
        }
      };

      /**
       * Triggered when recognition yields a result
       */
      this.recognition.onresult = (event: any) => {
        if (!this.hasStarted) {
          // Ignore premature results before engine fully starts
          return;
        }

        let interim = '';

        // Loop through results starting from the result index
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const res = event.results[i];
          const transcript = res[0].transcript;

          if (res.isFinal) {
            // Combine interim buffer with final result
            const full = (interimBuffer + ' ' + transcript).trim();
            interimBuffer = ''; // Clear buffer after final

            // Run inside Angular zone to update the UI
            this.zone.run(() => observer.next(full));
          } else {
            // Accumulate interim results for future combination
            interim += transcript;
          }
        }

        // Save interim for next round
        if (interim) {
          interimBuffer = interimBuffer + ' ' + interim;
        }
      };

      /**
       * Triggered when an error occurs in recognition
       */
      this.recognition.onerror = (event: any) => {
        console.error('[Speech] error', event.error);

        if (event.error === 'no-speech' && this.isListening) {
          // Silent/no-input case: try restarting
          this.recognition!.stop();

          setTimeout(() => {
            if (!this.isRecognitionRunning) {
              try {
                this.recognition!.start();
              } catch (e) {
                console.warn('Restart after no-speech failed:', e);
              }
            }
          }, 300);
          return;
        }

        // Propagate all other errors to observer
        this.zone.run(() => observer.error(event.error));
      };

      /**
       * Start recognition with slight delay (ensures mic is ready)
       */
      setTimeout(() => {
        try {
          this.recognition!.start();
        } catch (e) {
          console.warn('recognition.start() failed initially:', e);
          observer.error('Failed to start recognition');
        }
      }, 300);

      /**
       * Cleanup logic when the observer unsubscribes
       */
      return () => {
        this.isListening = false;
        try {
          this.recognition?.stop();
        } catch (err) {
          console.warn('Error on stop during cleanup:', err);
        }
      };
    });
  }

  /**
   * Public method to stop listening manually
   */
  stop() {
    this.isListening = false;

    // Only stop if recognition is currently running
    if (this.recognition && this.isRecognitionRunning) {
      this.recognition.stop();
    }
  }
}
